/**
 * Zapier sync lifecycle derivation, shared by the bookings-list "Zapier"
 * column and the booking-detail Zapier Sync History badge so the two views
 * can never disagree.
 *
 * Lifecycle of a booking's Zapier sync:
 *   scheduled  → payload not yet dispatched (deferred airport sync waiting in
 *                the pg_cron queue for flight details)
 *   send_failed → dispatch to the Zapier webhook itself failed
 *   awaiting   → webhook accepted the payload; waiting for the Zap's callback
 *                to confirm the Limo Anywhere reservation
 *   confirmed  → callback delivered the LA reservation ID (terminal, good)
 *   unconfirmed → watchdog exhausted its automatic retry and escalated
 *                 (terminal, bad — manual retry still available)
 *   none       → no Zapier activity (integration disabled / legacy booking)
 */

export type ZapierSyncStatus =
  | 'scheduled'
  | 'send_failed'
  | 'awaiting'
  | 'confirmed'
  | 'unconfirmed'
  | 'none'

// Subset of bookings columns the derivation needs. Optional so callers can
// pass rows fetched before `npm run db:types` picks up the new columns.
export interface ZapierSyncStatusFields {
  zapier_sync_deferred_until?: string | null
  zapier_synced_at?: string | null
  zapier_error?: string | null
  zapier_la_reservation_id?: string | null
  zapier_la_confirmed_at?: string | null
  zapier_la_final_alerted_at?: string | null
}

// Mirrors the watchdog's LOOKBACK_HOURS (check-zapier-la-confirmations):
// beyond this window the watchdog never acts, so "awaiting" would be a
// forever-amber badge. Bookings synced before the confirmation loop existed
// (or while the account's flag was off) fall out of the window and render as
// plain "no status" instead. Genuinely watched bookings reach a terminal
// state (confirmed/unconfirmed) within ~30 minutes, long before this cutoff.
const AWAITING_WINDOW_MS = 24 * 60 * 60 * 1000

export function deriveZapierSyncStatus(
  booking: ZapierSyncStatusFields
): ZapierSyncStatus {
  if (booking.zapier_la_reservation_id) return 'confirmed'
  if (booking.zapier_la_final_alerted_at) return 'unconfirmed'
  if (booking.zapier_synced_at) {
    const syncedAgoMs = Date.now() - new Date(booking.zapier_synced_at).getTime()
    return syncedAgoMs < AWAITING_WINDOW_MS ? 'awaiting' : 'none'
  }
  if (booking.zapier_error) return 'send_failed'
  if (booking.zapier_sync_deferred_until) return 'scheduled'
  return 'none'
}

export interface ZapierStatusBadgeConfig {
  label: string
  /** Tailwind classes applied to a Badge with variant="outline". */
  className: string
}

// 'none' intentionally absent — render an em dash / nothing instead of a badge.
export const ZAPIER_STATUS_BADGES: Record<
  Exclude<ZapierSyncStatus, 'none'>,
  ZapierStatusBadgeConfig
> = {
  scheduled: {
    label: 'Scheduled',
    className: 'border-sky-300 bg-sky-50 text-sky-700',
  },
  send_failed: {
    label: 'Send failed',
    className: 'border-red-300 bg-red-50 text-red-700',
  },
  awaiting: {
    label: 'Awaiting LA',
    className: 'border-amber-300 bg-amber-50 text-amber-700',
  },
  confirmed: {
    label: 'Confirmed',
    className: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  },
  unconfirmed: {
    label: 'Unconfirmed',
    className: 'border-red-300 bg-red-50 text-red-700',
  },
}

/** Tooltip text explaining the status on hover; null when nothing useful. */
export function zapierStatusTooltip(
  status: ZapierSyncStatus,
  booking: ZapierSyncStatusFields
): string | null {
  switch (status) {
    case 'scheduled':
      return booking.zapier_sync_deferred_until
        ? `Dispatches to Zapier ~${new Date(booking.zapier_sync_deferred_until).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}`
        : 'Waiting to dispatch to Zapier'
    case 'send_failed':
      return booking.zapier_error || 'The Zapier webhook call failed'
    case 'awaiting':
      return 'Sent to Zapier — waiting for the Zap to confirm the Limo Anywhere reservation'
    case 'confirmed':
      return booking.zapier_la_confirmed_at
        ? `LA reservation #${booking.zapier_la_reservation_id} confirmed ${new Date(booking.zapier_la_confirmed_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}`
        : `LA reservation #${booking.zapier_la_reservation_id}`
    case 'unconfirmed':
      return 'No Limo Anywhere confirmation after the automatic retry — check the Zap run history and retry manually'
    case 'none':
      return null
  }
}
